import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useGetJobCommuteLazyQuery } from './graphql/__generated__/useGetJobCommute.gqlgenerated';
import { useGetJobSeekerProfileLocationLazyQuery } from './graphql/__generated__/useGetJobSeekerProfileLocation.gqlgenerated';
import { useGetMaximumCommutePreferenceLazyQuery } from './graphql/__generated__/useGetMaximumCommutePreference.gqlgenerated';
import { Box, Pressable, Text, TextLink } from '../../../../../../../mads';
import { Car, Edit } from '../../../../../../../mads/icons';
import { MarkupComponents } from '../../../../../../../shared/components/MarkupComponents/MarkupComponents';
import { useI18n } from '../../../../../../../shared/I18n';
import { useStoredUserLocation } from '../../../../../../../shared/location/useStoredUserLocation';
import { useLogger } from '../../../../../../../shared/logging/logger';
import { useViewjobProctorAllocation } from '../../../../../../proctor/__generated__/ViewjobProctorContext';
import { MobVjCommutePrefTst } from '../../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import { useViewjobOneGraphModel } from '../../../../../context/ViewJobDataContext';

import { colors } from '@indeed/ifl-tokens/react-native';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';

type NavigationProp = NativeStackNavigationProp<
    { CommutePreferenceModal: undefined },
    'CommutePreferenceModal'
>;

export interface CommuteSectionProps {
    /** Commute duration in minutes from the saved address. Server-computed. */
    commuteTimeMinutes?: number;
}

export function CommuteSection({ commuteTimeMinutes }: CommuteSectionProps) {
    const gt = useI18n();
    const logger = useLogger();
    const navigation = useNavigation<NavigationProp>();
    const proctorValue = useViewjobProctorAllocation(MobVjCommutePrefTst).value;
    const isCommutePrefActive = proctorValue > 0;
    const {
        storedLocation,
        loading: storedLocationLoading,
        updateStoredLocation,
        clearStoredUserLocation,
    } = useStoredUserLocation();

    // Profile location + max-commute data is fetched as part of the main
    // viewjob query on the GraphQL path. On the legacy AppContent path the
    // main query doesn't run, so we fall back to the standalone lazy queries
    // below.
    const oneGraph = useViewjobOneGraphModel();
    const oneGraphProfileLocation = oneGraph?.jobSeekerProfileLocation;
    const oneGraphMaxCommuteMinutes = oneGraph?.maxCommuteMinutes;
    const hasOneGraphData = oneGraphProfileLocation !== undefined;

    // Reconcile localPrefs with server state once per mount. After this,
    // localPrefs is the source of truth for the UI — the modal's save flow
    // keeps server and localPrefs in sync for in-app changes
    const [fetchProfileLocation, { data: profileLocationData }] =
        useGetJobSeekerProfileLocationLazyQuery({ fetchPolicy: 'no-cache' });
    const [fetchMaxCommute, { data: maxCommuteData }] =
        useGetMaximumCommutePreferenceLazyQuery({ fetchPolicy: 'no-cache' });
    useEffect(() => {
        // GraphQL path already loaded the data via the main viewjob query.
        if (hasOneGraphData) return;
        void fetchProfileLocation();
    }, [hasOneGraphData, fetchProfileLocation]);
    useEffect(() => {
        if (!isCommutePrefActive) return;
        if (hasOneGraphData) return;
        void fetchMaxCommute();
    }, [isCommutePrefActive, hasOneGraphData, fetchMaxCommute]);

    // Unified source for the reconciliation effects below — either path.
    const sourceLocation = hasOneGraphData
        ? oneGraphProfileLocation?.sourceLocation
        : (() => {
              const profile = profileLocationData?.jobSeekerProfile?.profile;
              const mainResume = profile?.resume?.[0];
              return mainResume?.id
                  ? mainResume.location
                  : profile?.defaultInfo?.contactInformation?.location;
          })();
    const profileLocationReady = hasOneGraphData || profileLocationData !== undefined;
    const serverMaxCommuteRaw = hasOneGraphData
        ? oneGraphMaxCommuteMinutes
        : maxCommuteData?.jobSeekerProfileStructuredData?.preferences?.[0]?.maximumCommute
              ?.timeMinutes;
    const serverMaxCommute =
        serverMaxCommuteRaw && serverMaxCommuteRaw > 0 ? serverMaxCommuteRaw : undefined;
    const maxCommuteReady = hasOneGraphData || maxCommuteData !== undefined;

    const [hasInitialSyncedProfileLocation, setHasInitialSyncedProfileLocation] =
        useState(false);
    useEffect(() => {
        if (hasInitialSyncedProfileLocation) return;
        if (storedLocationLoading) return;
        if (!profileLocationReady) return;
        const serverAddress = sourceLocation?.address;
        const serverLat = sourceLocation?.latitude;
        const serverLon = sourceLocation?.longitude;
        if (serverAddress && serverLat != null && serverLon != null) {
            if (
                storedLocation?.streetAddress !== serverAddress ||
                storedLocation.latitude !== serverLat ||
                storedLocation.longitude !== serverLon
            ) {
                void updateStoredLocation({
                    latitude: serverLat,
                    longitude: serverLon,
                    streetAddress: serverAddress,
                    formattedLocation: sourceLocation?.formattedLocation ?? undefined,
                    city: sourceLocation?.city ?? undefined,
                    admin1: sourceLocation?.admin1 ?? undefined,
                    admin2: sourceLocation?.admin2 ?? undefined,
                    admin3: sourceLocation?.admin3 ?? undefined,
                    admin4: sourceLocation?.admin4 ?? undefined,
                    postalCode: sourceLocation?.postalCode ?? undefined,
                    country: sourceLocation?.country ?? undefined,
                    maxCommuteMinutes: storedLocation?.maxCommuteMinutes ?? null,
                });
            }
        } else if (storedLocation) {
            void clearStoredUserLocation();
        }
        setHasInitialSyncedProfileLocation(true);
    }, [
        hasInitialSyncedProfileLocation,
        profileLocationReady,
        sourceLocation,
        storedLocation,
        storedLocationLoading,
        updateStoredLocation,
        clearStoredUserLocation,
    ]);

    // Max-commute sync waits for the profile sync so storedLocation is
    // settled before we try to merge maxCommuteMinutes into it.
    const [hasInitialSyncedMaxCommute, setHasInitialSyncedMaxCommute] = useState(false);
    useEffect(() => {
        if (!isCommutePrefActive) return;
        if (hasInitialSyncedMaxCommute) return;
        if (!hasInitialSyncedProfileLocation) return;
        if (!maxCommuteReady) return;
        if (!storedLocation) {
            setHasInitialSyncedMaxCommute(true);
            return;
        }
        if (serverMaxCommute !== undefined) {
            if (storedLocation.maxCommuteMinutes !== serverMaxCommute) {
                void updateStoredLocation({
                    ...storedLocation,
                    maxCommuteMinutes: serverMaxCommute,
                });
            }
        } else if (storedLocation.maxCommuteMinutes != null) {
            void updateStoredLocation({
                ...storedLocation,
                maxCommuteMinutes: null,
            });
        }
        setHasInitialSyncedMaxCommute(true);
    }, [
        isCommutePrefActive,
        hasInitialSyncedMaxCommute,
        hasInitialSyncedProfileLocation,
        maxCommuteReady,
        serverMaxCommute,
        storedLocation,
        updateStoredLocation,
    ]);

    const address = storedLocation?.streetAddress;

    // When the SSR JSON's `commuteTimeMinutes` is missing, fall back to a
    // direct OneGraph `Job.commute` query. The SSR value is computed
    // server-side at page load against the *server-side* profile location:
    //
    //   - Desktop web: SSR omits it entirely (the indeedmobile model factory
    //     only computes commute when `mobileEnvironment.isApp()` is true).
    //   - Mobile native: SSR includes it, but only reflects whatever address
    //     the server had at load time — so it's stale after the user edits
    //     their address client-side (no SSR refetch on mutation).

    const jobKey = oneGraph?.job?.jobKey;
    const jobLatitude = oneGraph?.job?.location?.jobLatitude;
    const jobLongitude = oneGraph?.job?.location?.jobLongitude;
    const [fetchJobCommute, { data: jobCommuteData }] = useGetJobCommuteLazyQuery({
        fetchPolicy: 'no-cache',
    });
    useEffect(() => {
        if (commuteTimeMinutes != null) return;
        if (!jobKey) return;
        if (!storedLocation?.latitude || !storedLocation?.longitude) return;
        if (jobLatitude == null || jobLongitude == null) return;
        void fetchJobCommute({
            variables: {
                jobKey,
                commuteFrom: {
                    latitude: storedLocation.latitude,
                    longitude: storedLocation.longitude,
                },
            },
        });
    }, [
        commuteTimeMinutes,
        jobKey,
        storedLocation?.latitude,
        storedLocation?.longitude,
        jobLatitude,
        jobLongitude,
        fetchJobCommute,
    ]);

    const effectiveCommuteMinutes =
        commuteTimeMinutes ?? jobCommuteData?.viewjob?.job?.commute?.durationMinutes ?? undefined;

    // A commute over 2 hours is treated as "no usable estimate" — the
    // section hides entirely in that case. Between 60 and 120 minutes we
    // collapse to the "Over an hour commute" message.
    const commuteEstimateText =
        !effectiveCommuteMinutes || effectiveCommuteMinutes > 120
            ? undefined
            : effectiveCommuteMinutes > 60
              ? gt.gettext('Over an hour commute')
              : gt.gettext('{0} minute commute', [effectiveCommuteMinutes]);

    const openModal = useCallback(() => {
        logger.event.trackEvent('interactiveAttributeInteraction', {
            action: 'OPEN',
            section: 'location',
        });
        navigation.navigate('CommutePreferenceModal');
    }, [logger, navigation]);

    if (!isCommutePrefActive) {
        if (!commuteEstimateText) {
            return null;
        }
        return (
            <Box sx={styles.section}>
                <Box sx={styles.tileSectionRow}>
                    <Box sx={styles.iconContainer}>
                        <Car size="sm" variant="filled" fill={colors.foregroundStrong} />
                    </Box>
                    <Box sx={styles.contentContainer}>
                        <Text level={3} sx={styles.tileValueText}>
                            {commuteEstimateText}
                        </Text>
                    </Box>
                </Box>
            </Box>
        );
    }

    const hasCommutePreference =
        storedLocation?.maxCommuteMinutes != null && storedLocation.maxCommuteMinutes > 0;
    // Defer the "Add commute preference" CTA until the server query has
    // confirmed the user has no preference — otherwise users with a
    // server-side preference would see a stale CTA on initial load.
    const hasConfirmedNoCommutePreference =
        hasInitialSyncedMaxCommute && !hasCommutePreference;

    // Render rules:
    //   - No address (confirmed by sync) + has lat/lon       → "Add location for commute" CTA
    //   - Address + commute time + has commute pref          → "{N} minute commute" + "from {address}" + edit
    //   - Address + commute time + confirmed no pref         → "{N} minute commute" + plain "from {address}" + "Add commute preference" CTA
    //   - Address + commute time + pref unknown (loading)    → "{N} minute commute" + "from {address}" + edit (optimistic)
    //   - Address + no commute time                          → null
    //   - No address + sync still loading                    → null (avoids flashing the CTA on initial render)
    //   - No address + no lat/lon                            → null
    let sectionContent: React.ReactNode = null;
    if (address && commuteEstimateText && !hasConfirmedNoCommutePreference) {
        sectionContent = (
            <CommuteRow>
                <Text level={3} sx={styles.tileValueText}>
                    {commuteEstimateText}
                </Text>
                <Box sx={styles.fromRow}>
                    <Text variant="contrast" level={3}>
                        <MarkupComponents
                            components={{
                                // eslint-disable-next-line react/no-unstable-nested-components -- render-prop callback invoked by MarkupComponents, not a React component
                                address: (text) => (
                                    <TextLink
                                        level={3}
                                        onPress={openModal}
                                        aria-label={gt.gettext('Edit location: {0}', [address])}
                                    >
                                        {text}
                                    </TextLink>
                                ),
                            }}
                        >
                            {gt.gettext('from [{0}|address]', [address])}
                        </MarkupComponents>
                    </Text>
                    <Pressable
                        onPress={openModal}
                        accessibilityRole="button"
                        accessibilityLabel={gt.gettext('Edit location preferences')}
                        sx={styles.editIconButton}
                    >
                        <Edit
                            size="sm"
                            variant="stroke"
                            fill={colors.foregroundActionPrimary}
                        />
                    </Pressable>
                </Box>
            </CommuteRow>
        );
    } else if (address && commuteEstimateText) {
        sectionContent = (
            <CommuteRow>
                <Text level={3} sx={styles.tileValueText}>
                    {commuteEstimateText}
                </Text>
                <Text variant="contrast" level={3}>
                    {gt.gettext('from {0}', [address])}
                </Text>
                <Pressable
                    onPress={openModal}
                    accessibilityRole="button"
                    accessibilityLabel={gt.gettext('Add commute preference')}
                >
                    <Text sx={styles.addLocationLink}>
                        {gt.gettext('Add commute preference')}
                    </Text>
                </Pressable>
            </CommuteRow>
        );
    } else if (!address && hasInitialSyncedProfileLocation) {
        sectionContent = (
            <CommuteRow>
                <Pressable
                    onPress={openModal}
                    accessibilityRole="button"
                    accessibilityLabel={gt.gettext('Add location for commute')}
                >
                    <Text sx={styles.addLocationLink}>
                        {gt.gettext('Add location for commute')}
                    </Text>
                </Pressable>
            </CommuteRow>
        );
    }

    if (!sectionContent) {
        return null;
    }

    return <Box sx={styles.section}>{sectionContent}</Box>;
}

/** Shared layout for a commute section row: car icon on the left, content on the right. */
function CommuteRow({ children }: { children: React.ReactNode }) {
    return (
        <Box sx={styles.tileSectionRow}>
            <Box sx={styles.iconContainer}>
                <Car size="sm" variant="filled" fill={colors.foregroundStrong} />
            </Box>
            <Box sx={styles.contentContainer}>{children}</Box>
        </Box>
    );
}

const styles = StyleSheet.create({
    section: {
        marginBottom: 16,
    },
    tileSectionRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'flex-start',
    },
    iconContainer: {
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: 2,
    },
    contentContainer: {
        flex: 1,
        gap: 4,
    },
    tileValueText: {
        fontWeight: 'bold',
        color: colors.foregroundStrong,
    },
    fromRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
    },
    addLocationLink: {
        fontSize: 16,
        fontWeight: '500',
        color: colors.foregroundActionPrimary,
    },
    editIconButton: {
        marginLeft: 4,
    },
});
